import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { projectList as defaultProjects, aboutList as defaultAbout, lesureList as defaultLeisure, Reads as defaultReads } from "../utils/list";

const STORAGE_KEY = "mo_portfolio_data";
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:4000/api";

const PortfolioContext = createContext(null);

export function PortfolioProvider({ children }) {
  const [projects, setProjectsState] = useState(defaultProjects);
  const [about, setAboutState]       = useState(defaultAbout);
  const [reads, setReadsState]       = useState(defaultReads);
  const [leisure, setLeisureState]   = useState(defaultLeisure);

  /* try server first, fall back to localStorage */
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/portfolio`);
        if (!res.ok) throw new Error('no-server');
        const data = await res.json();
        if (mounted && Object.keys(data).length) {
          if (data.projects) setProjectsState(data.projects);
          if (data.about)    setAboutState(data.about);
          if (data.reads)    setReadsState(data.reads);
          if (data.leisure)  setLeisureState(data.leisure);
          return;
        }
        // empty DB: initialize with defaults
        await fetch(`${API_BASE}/portfolio`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projects: defaultProjects, about: defaultAbout, reads: defaultReads, leisure: defaultLeisure })
        });
        // states already set to defaults above by useState initializers
      } catch (e) {
        try {
          const stored = localStorage.getItem(STORAGE_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.projects) setProjectsState(parsed.projects);
            if (parsed.about)    setAboutState(parsed.about);
            if (parsed.reads)    setReadsState(parsed.reads);
            if (parsed.leisure)  setLeisureState(parsed.leisure);
          }
        } catch { /* ignore malformed storage */ }
      }
    })();
    return () => { mounted = false; };
  }, []);

  const persistToLocal = useCallback((key, value) => {
    try {
      const current = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, [key]: value }));
    } catch { /* ignore */ }
  }, []);

  const persist = useCallback(async (key, value) => {
    try {
      const res = await fetch(`${API_BASE}/portfolio/${encodeURIComponent(key)}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(value)
      });
      if (!res.ok) throw new Error('server');
    } catch (e) {
      // fallback to localStorage
      persistToLocal(key, value);
    }
  }, [persistToLocal]);

  const setProjects = (v) => { const val = typeof v === "function" ? v(projects) : v; setProjectsState(val); persist('projects', val); };
  const setAbout    = (v) => { const val = typeof v === "function" ? v(about)    : v; setAboutState(val);    persist('about',    val); };
  const setReads    = (v) => { const val = typeof v === "function" ? v(reads)    : v; setReadsState(val);    persist('reads',    val); };
  const setLeisure  = (v) => { const val = typeof v === "function" ? v(leisure)  : v; setLeisureState(val);  persist('leisure',  val); };

  const resetToDefaults = async () => {
    setProjectsState(defaultProjects); setAboutState(defaultAbout);
    setReadsState(defaultReads);       setLeisureState(defaultLeisure);
    try {
      await fetch(`${API_BASE}/portfolio/reset`, { method: 'POST' });
    } catch { localStorage.removeItem(STORAGE_KEY); }
    // also clear local cache
    try { localStorage.removeItem(STORAGE_KEY); } catch {}
  };

  return (
    <PortfolioContext.Provider value={{ projects, setProjects, about, setAbout, reads, setReads, leisure, setLeisure, resetToDefaults }}>
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolio() {
  const ctx = useContext(PortfolioContext);
  if (!ctx) throw new Error("usePortfolio must be used within PortfolioProvider");
  return ctx;
}
